import React, { useState, useEffect, useContext, useMemo, useRef } from "react";
import { makeStyles, useTheme } from "@material-ui/core/styles";
import {
  Typography,
  CircularProgress,
  IconButton,
  Tooltip,
  Button,
  TextField,
  MenuItem,
  InputAdornment,
} from "@material-ui/core";
import OpenInNewIcon from "@material-ui/icons/OpenInNew";
import RefreshIcon from "@material-ui/icons/Refresh";
import HelpOutlineIcon from "@material-ui/icons/HelpOutline";
import ZoomInIcon from "@material-ui/icons/ZoomIn";
import ZoomOutIcon from "@material-ui/icons/ZoomOut";
import SearchIcon from "@material-ui/icons/Search";
import LabelOutlinedIcon from "@material-ui/icons/LabelOutlined";
import api from "../../services/api";
import { AuthContext } from "../../context/Auth/AuthContext";
import { socketConnection } from "../../services/socket";
import Board from "react-trello";
import { toast } from "react-toastify";
import { useHistory } from "react-router-dom";
import ContactAvatar from "../../components/ContactAvatar";
import formatPhone from "../../helpers/formatPhone";
import {
  useKanbanDndStyles,
  boardZoomStyle,
  DropHighlightLane,
  MetaLaneHeader,
  shortSince,
} from "../../components/KanbanDnd";

const ZOOM_KEY = "kanbanZoom";
const ZOOM_STEP = 0.1;
const ZOOM_MIN = 0.5;
const ZOOM_MAX = 1.0;
const EMPTY_CARD_PREFIX = "__empty__";

const useStyles = makeStyles((theme) => ({
  root: {
    display: "flex",
    flexDirection: "column",
    height: "100%",
    overflow: "hidden",
    // o CssBaseline do app fica fora do ThemeProvider: sem isso o texto
    // herda o preto do tema claro no tema escuro
    color: theme.palette.text.primary,
  },
  header: {
    display: "flex",
    alignItems: "flex-end",
    justifyContent: "space-between",
    flexWrap: "wrap",
    gap: theme.spacing(2),
    padding: theme.spacing(2.5, 3, 2),
    flexShrink: 0,
  },
  title: {
    fontSize: 26,
    fontWeight: 700,
    color: theme.palette.text.primary,
    lineHeight: 1.2,
  },
  subtitle: {
    fontSize: 13,
    color: theme.palette.text.secondary,
    marginTop: 4,
  },
  headerButton: {
    textTransform: "none",
    fontWeight: 600,
    borderRadius: 10,
    height: 40,
    padding: theme.spacing(0, 2),
    color: theme.palette.text.primary,
    borderColor: theme.palette.divider,
    backgroundColor: theme.palette.background.paper,
  },
  toolbar: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1.5),
    flexWrap: "wrap",
    padding: theme.spacing(0, 3, 1.5),
    flexShrink: 0,
  },
  searchField: {
    width: 320,
    "& .MuiOutlinedInput-root": {
      borderRadius: 10,
      backgroundColor: theme.palette.background.paper,
      height: 38,
    },
    [theme.breakpoints.down("xs")]: { width: "100%" },
  },
  queueSelect: {
    minWidth: 190,
    "& .MuiOutlinedInput-root": {
      borderRadius: 10,
      backgroundColor: theme.palette.background.paper,
      height: 38,
    },
  },
  zoomGroup: {
    display: "flex",
    alignItems: "center",
    gap: 2,
    marginLeft: "auto",
    color: theme.palette.text.secondary,
  },
  zoomLabel: {
    fontSize: "0.72rem",
    color: theme.palette.text.secondary,
    minWidth: 34,
    textAlign: "center",
    userSelect: "none",
  },
  boardWrapper: {
    flex: 1,
    overflow: "hidden",
    position: "relative",
    "& *::-webkit-scrollbar": { width: 4, height: 4 },
    "& *::-webkit-scrollbar-thumb": {
      borderRadius: 4,
      backgroundColor: "transparent",
    },
    "& *:hover::-webkit-scrollbar-thumb": {
      backgroundColor: theme.palette.grey[500],
    },
    "& *::-webkit-scrollbar-track": { background: "transparent" },
  },
  loadingOverlay: {
    position: "absolute",
    inset: 0,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: theme.palette.type === "dark" ? "rgba(0,0,0,0.5)" : "rgba(255,255,255,0.7)",
    zIndex: 10,
  },

  // ── Card ──
  card: {
    backgroundColor: theme.palette.background.paper,
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: 10,
    padding: 12,
    marginBottom: 8,
    cursor: "grab",
    display: "flex",
    flexDirection: "column",
    gap: 8,
    boxShadow: "0 1px 3px rgba(0,0,0,0.08)",
    "&:hover": {
      boxShadow: "0 4px 12px rgba(0,0,0,0.18)",
      transform: "translateY(-1px)",
    },
    transition: "box-shadow 0.15s ease, transform 0.15s ease",
    width: 240,
    minWidth: 240,
    maxWidth: 240,
    boxSizing: "border-box",
    overflow: "hidden",
    whiteSpace: "normal", // o react-trello força nowrap nas colunas
  },
  emptyCard: {
    width: 240,
    minWidth: 240,
    maxWidth: 240,
    padding: "20px 0",
    textAlign: "center",
    fontSize: "0.8rem",
    color: theme.palette.text.secondary,
    userSelect: "none",
    cursor: "default",
  },
  cardHeader: {
    display: "flex",
    alignItems: "center",
    gap: 10,
    minWidth: 0,
  },
  cardIdentity: {
    flex: 1,
    minWidth: 0,
    display: "flex",
    flexDirection: "column",
  },
  cardName: {
    fontWeight: 600,
    fontSize: 14,
    color: theme.palette.text.primary,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  cardPhone: {
    fontSize: 12,
    color: theme.palette.text.secondary,
    fontVariantNumeric: "tabular-nums",
  },
  cardMessage: {
    fontSize: 13,
    lineHeight: "18px",
    color: theme.palette.text.secondary,
    overflow: "hidden",
    display: "-webkit-box",
    WebkitLineClamp: 2,
    WebkitBoxOrient: "vertical",
    wordBreak: "break-word",
  },
  cardFooter: {
    display: "flex",
    alignItems: "center",
    gap: 6,
  },
  queueBadge: {
    display: "inline-flex",
    alignItems: "center",
    gap: 5,
    height: 22,
    padding: "0 8px",
    borderRadius: 999,
    fontSize: 11,
    fontWeight: 600,
    maxWidth: 130,
    overflow: "hidden",
    whiteSpace: "nowrap",
    textOverflow: "ellipsis",
    color: theme.palette.text.primary,
    backgroundColor: theme.palette.action.selected,
  },
  queueDot: {
    width: 7,
    height: 7,
    borderRadius: "50%",
    flexShrink: 0,
  },
  ticketId: {
    fontSize: 11,
    color: theme.palette.text.secondary,
    fontVariantNumeric: "tabular-nums",
  },
  cardTime: {
    marginLeft: "auto",
    fontSize: 11,
    color: theme.palette.text.secondary,
    whiteSpace: "nowrap",
  },
  openIcon: {
    padding: 2,
    color: theme.palette.text.secondary,
    flexShrink: 0,
    "&:hover": { color: theme.palette.primary.main },
  },
}));

// ---------------------------------------------------------------------------
// Card do ticket
// ---------------------------------------------------------------------------
const KanbanCard = ({ title, description, classes, history }) => {
  const {
    ticketId,
    message,
    uuid,
    number,
    profilePicUrl,
    queueName,
    queueColor,
    updatedAt,
    isEmpty,
  } = description || {};

  if (isEmpty) {
    return <div className={classes.emptyCard}>Nenhum atendimento nesta etapa</div>;
  }

  return (
    <div className={classes.card}>
      <div className={classes.cardHeader}>
        <ContactAvatar src={profilePicUrl} name={title} style={{ width: 32, height: 32, fontSize: 14 }} />
        <div className={classes.cardIdentity}>
          <Typography className={classes.cardName} title={title}>
            {title}
          </Typography>
          {number && <span className={classes.cardPhone}>{formatPhone(number) || number}</span>}
        </div>
        <Tooltip title="Abrir conversa">
          <IconButton
            className={classes.openIcon}
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              history.push(`/tickets/${uuid}`);
            }}
          >
            <OpenInNewIcon style={{ fontSize: 14 }} />
          </IconButton>
        </Tooltip>
      </div>

      {message && <Typography className={classes.cardMessage}>{message}</Typography>}

      <div className={classes.cardFooter}>
        {queueName && (
          <span className={classes.queueBadge} title={`Departamento: ${queueName}`}>
            <span className={classes.queueDot} style={{ backgroundColor: queueColor || "#9E9E9E" }} />
            {queueName}
          </span>
        )}
        <span className={classes.ticketId}>#{ticketId}</span>
        {updatedAt && (
          <Tooltip title="Última atividade">
            <span className={classes.cardTime}>{shortSince(updatedAt)}</span>
          </Tooltip>
        )}
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Kanban Page
// ---------------------------------------------------------------------------
const Kanban = () => {
  const classes = useStyles();
  const dndClasses = useKanbanDndStyles();
  const theme = useTheme();
  const history = useHistory();
  const { user } = useContext(AuthContext);

  const [tags, setTags] = useState([]);
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [queueFilter, setQueueFilter] = useState("");
  const isAdminOrSupervisor = ["admin", "supervisor"].includes(user?.profile);
  const [zoom, setZoom] = useState(() => {
    const saved = parseFloat(localStorage.getItem(ZOOM_KEY));
    return !isNaN(saved) ? saved : 1.0;
  });

  const queueIds = user.queues.map((q) => q.UserQueue.queueId);

  // Só a resposta da busca mais recente é aplicada: com várias buscas em
  // paralelo (socket + drag), uma resposta antiga poderia desfazer o
  // movimento otimista que acabou de ser feito.
  const fetchSeq = useRef(0);

  // `silent` atualiza sem cobrir o board com o loading — usado depois de
  // arrastar e nos eventos de socket, que chegam a cada mensagem nova.
  const fetchData = async ({ silent = false } = {}) => {
    const seq = ++fetchSeq.current;
    if (!silent) setLoading(true);
    try {
      const [tagsRes, ticketsRes] = await Promise.all([
        api.get("/tags/kanban"),
        api.get("/ticket/kanban", {
          params: { queueIds: JSON.stringify(queueIds), teste: true },
        }),
      ]);
      // A API já devolve ordenado por kanbanOrder; o sort local mantém o
      // board correto mesmo se a resposta vier de outra origem, igual ao
      // OpportunityKanban faz com os estágios do pipeline.
      const kanbanTags = [...(tagsRes.data.lista || [])].sort(
        (a, b) => (a.kanbanOrder ?? 0) - (b.kanbanOrder ?? 0)
      );
      if (seq !== fetchSeq.current) return;
      setTags(kanbanTags);
      setTickets(ticketsRes.data.tickets || []);
    } catch (err) {
      console.error(err);
    } finally {
      if (seq === fetchSeq.current) setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    const companyId = localStorage.getItem("companyId");
    const socket = socketConnection({ companyId });

    socket.on(`company-${companyId}-ticket`, (data) => {
      if (["update", "create", "delete"].includes(data.action)) {
        fetchData({ silent: true });
      }
    });

    // Reordenar as raias na tela de Tags precisa refletir nos boards abertos.
    socket.on(`company-${companyId}-tag`, (data) => {
      if (data.action === "reorder") {
        fetchData({ silent: true });
      }
    });

    return () => socket.disconnect();
  }, []);

  const handleZoom = (delta) => {
    setZoom((prev) => {
      const next = Math.min(ZOOM_MAX, Math.max(ZOOM_MIN, Math.round((prev + delta) * 10) / 10));
      localStorage.setItem(ZOOM_KEY, String(next));
      return next;
    });
  };

  const query = searchQuery.trim().toLowerCase();
  const queryDigits = query.replace(/\D/g, "");
  const visibleTickets = tickets.filter((ticket) => {
    if (queueFilter && String(ticket.queue?.id ?? ticket.queueId) !== String(queueFilter)) return false;
    if (!query) return true;
    return (
      ticket.contact?.name?.toLowerCase().includes(query) ||
      ticket.lastMessage?.toLowerCase().includes(query) ||
      String(ticket.id) === query ||
      (queryDigits.length >= 3 && String(ticket.contact?.number || "").includes(queryDigits))
    );
  });

  const buildBoard = () => {
    const makeCards = (list, laneId) => {
      if (list.length === 0) {
        return [{
          id: `${EMPTY_CARD_PREFIX}${laneId}`,
          title: "",
          description: { isEmpty: true },
          draggable: false,
        }];
      }
      return list.map((ticket) => ({
        id: `${ticket.id}:${laneId}`,
        title: ticket.contact.name,
        label: `#${ticket.id}`,
        description: {
          ticketId: ticket.id,
          message: ticket.lastMessage,
          uuid: ticket.uuid,
          number: ticket.contact.number,
          profilePicUrl: ticket.contact.profilePicUrl,
          queueName: ticket.queue?.name,
          queueColor: ticket.queue?.color,
          updatedAt: ticket.updatedAt,
        },
        draggable: true,
      }));
    };

    const laneStyle = (color) => ({
      backgroundColor: theme.palette.background.paper,
      border: `1px solid ${theme.palette.divider}`,
      borderTop: `3px solid ${color}`,
      borderRadius: 12,
      height: "100%",
      maxHeight: "100%",
      padding: "10px 8px",
    });

    const kanbanTagIds = new Set(tags.map((t) => Number(t.id)));

    const getTicketLane = (ticket) => {
      for (const tag of (ticket.tags || [])) {
        if (kanbanTagIds.has(Number(tag.id))) return String(tag.id);
      }
      return "open";
    };

    const openTickets = visibleTickets.filter((t) => getTicketLane(t) === "open");
    const noTagColor = theme.palette.type === "dark" ? "#78909C" : "#90A4AE";

    const lanes = [
      {
        id: "open",
        title: "Sem etapa",
        laneMeta: {
          color: noTagColor,
          count: openTickets.length,
          caption: "Ainda sem etiqueta de Kanban",
        },
        style: laneStyle(noTagColor),
        cards: makeCards(openTickets, "open"),
      },
      ...tags.map((tag) => {
        const laneId = tag.id.toString();
        const tagTickets = visibleTickets.filter((t) => getTicketLane(t) === laneId);
        return {
          id: laneId,
          title: tag.name,
          laneMeta: {
            color: tag.color || "#90A4AE",
            count: tagTickets.length,
            caption: tagTickets.length === 1 ? "1 atendimento" : `${tagTickets.length} atendimentos`,
          },
          style: laneStyle(tag.color || "#90A4AE"),
          cards: makeCards(tagTickets, laneId),
        };
      }),
    ];

    return { lanes };
  };

  const handleCardMove = async (fromLaneId, toLaneId, cardId) => {
    if (cardId.startsWith(EMPTY_CARD_PREFIX)) return;
    if (fromLaneId === toLaneId) return;
    const ticketId = cardId.split(":")[0];

    // Atualização otimista: o card já fica na raia nova (com contadores e
    // placeholder de raia vazia corretos) sem esperar a API.
    const kanbanTagIds = new Set(tags.map((t) => Number(t.id)));
    const toTag = tags.find((t) => String(t.id) === toLaneId);
    setTickets((prev) =>
      prev.map((ticket) => {
        if (String(ticket.id) !== ticketId) return ticket;
        const otherTags = (ticket.tags || []).filter(
          (tag) => !kanbanTagIds.has(Number(tag.id))
        );
        return { ...ticket, tags: toTag ? [...otherTags, toTag] : otherTags };
      })
    );

    try {
      if (fromLaneId !== "open") {
        await api.delete(`/ticket-tags/${ticketId}`);
      }
      if (toLaneId !== "open") {
        await api.put(`/ticket-tags/${ticketId}/${toLaneId}`);
      }
    } catch (err) {
      toast.error("Erro ao mover ticket.");
    } finally {
      // confirma com o servidor (e desfaz o movimento se a API falhou)
      fetchData({ silent: true });
    }
  };

  // Componentes estáveis entre renders: criados inline, cada render virava um
  // tipo novo de componente e todos os cards eram desmontados e montados de
  // novo (piscando) a cada atualização do board.
  const boardComponents = useMemo(
    () => ({
      Card: (props) => (
        <KanbanCard {...props} classes={classes} history={history} />
      ),
      LaneHeader: MetaLaneHeader,
      ScrollableLane: DropHighlightLane,
    }),
    [classes, history]
  );

  const boardData = buildBoard();
  const userQueues = user.queues || [];

  return (
    <div className={classes.root}>
      {/* Cabeçalho */}
      <div className={classes.header}>
        <div>
          <Typography component="h1" className={classes.title}>
            Kanban
          </Typography>
          <Typography className={classes.subtitle}>
            {visibleTickets.length} {visibleTickets.length === 1 ? "atendimento" : "atendimentos"} dos seus departamentos ·
            arraste o card para mudar a etapa
          </Typography>
        </div>
        {isAdminOrSupervisor && (
          <Button
            variant="outlined"
            className={classes.headerButton}
            startIcon={<LabelOutlinedIcon />}
            onClick={() => history.push("/tags")}
          >
            Gerenciar etapas
          </Button>
        )}
      </div>

      {/* Filtros */}
      <div className={classes.toolbar}>
        <TextField
          className={classes.searchField}
          variant="outlined"
          size="small"
          type="search"
          placeholder="Buscar contato ou mensagem"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          inputProps={{ "aria-label": "Buscar atendimento" }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon style={{ color: theme.palette.text.secondary }} />
              </InputAdornment>
            ),
          }}
        />
        {userQueues.length > 1 && (
          <TextField
            select
            className={classes.queueSelect}
            variant="outlined"
            size="small"
            value={queueFilter}
            onChange={(e) => setQueueFilter(e.target.value)}
            SelectProps={{ displayEmpty: true }}
            inputProps={{ "aria-label": "Departamento" }}
          >
            <MenuItem value="">Todos os departamentos</MenuItem>
            {userQueues.map((queue) => (
              <MenuItem key={queue.id} value={queue.id}>
                <span
                  style={{
                    width: 8,
                    height: 8,
                    borderRadius: "50%",
                    marginRight: 10,
                    display: "inline-block",
                    backgroundColor: queue.color || "#9E9E9E",
                  }}
                />
                {queue.name}
              </MenuItem>
            ))}
          </TextField>
        )}

        <div className={classes.zoomGroup}>
          <Tooltip title="Diminuir zoom">
            <span>
              <IconButton size="small" onClick={() => handleZoom(-ZOOM_STEP)} disabled={zoom <= ZOOM_MIN}>
                <ZoomOutIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
          <Typography className={classes.zoomLabel}>{Math.round(zoom * 100)}%</Typography>
          <Tooltip title="Aumentar zoom">
            <span>
              <IconButton size="small" onClick={() => handleZoom(ZOOM_STEP)} disabled={zoom >= ZOOM_MAX}>
                <ZoomInIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
          <Tooltip
            title={
              <span>
                As colunas são formadas pelas <strong>etiquetas marcadas como Kanban</strong> na tela de Etiquetas.
                <br /><br />
                Cada atendimento aparece em <strong>apenas uma coluna</strong> — a da primeira etiqueta Kanban encontrada.
                Ao arrastar para outra coluna, a tag anterior é removida automaticamente.
                <br /><br />
                Só aparecem atendimentos dos <strong>departamentos do seu usuário</strong>.
              </span>
            }
            placement="bottom-end"
            arrow
          >
            <IconButton size="small" aria-label="Ajuda">
              <HelpOutlineIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Atualizar">
            <span>
              <IconButton size="small" onClick={() => fetchData()} disabled={loading}>
                <RefreshIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
        </div>
      </div>

      {/* Board */}
      <div className={classes.boardWrapper}>
        {loading && (
          <div className={classes.loadingOverlay}>
            <CircularProgress size={36} />
          </div>
        )}
        <div className={dndClasses.boardZoom} style={boardZoomStyle(zoom)}>
          <Board
            data={boardData}
            onCardMoveAcrossLanes={handleCardMove}
            hideCardDeleteIcon
            cardDragClass={dndClasses.cardDragging}
            style={{
              backgroundColor: "transparent",
              height: "100%",
              padding: "0 16px 16px",
              alignItems: "stretch",
            }}
            components={boardComponents}
          />
        </div>
      </div>
    </div>
  );
};

export default Kanban;
